import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useQuery } from "@tanstack/react-query";
import { RedoPlatformAuthRpcClientContext } from "../platform/redo-platform-rpc-client-provider";

export interface SupportHelpdeskV2EnabledState {
  readonly pending: boolean;
  readonly enabled: boolean;
}

/**
 * Loads the `SUPPORT_HELPDESK_V2` feature flag for the current team.
 *
 * While `pending`, callers should render normally but skip any dual-write
 * side effects that depend on the flag — they'll resolve on the next save.
 * Once resolved, React Query caches the value across consumers, so the
 * settings pages share a single fetch even when the hook is mounted from
 * multiple subtrees.
 */
export function useSupportHelpdeskV2Enabled(): SupportHelpdeskV2EnabledState {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );

  const query = useQuery({
    queryKey: ["featureFlag", FeatureFlagKey.SUPPORT_HELPDESK_V2] as const,
    queryFn: async () => {
      const response = await platformRpcClient.getFeatureFlag({
        flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2,
      });
      return response.enabled;
    },
    staleTime: Infinity,
  });

  return { pending: query.isPending, enabled: query.data ?? false };
}
